import { useCallback, useEffect, useState } from "react";
import {
  buildBrandFormData,
  getBrandById,
  saveBrand,
  updateBrand,
} from "../../../apiService/loyalty.service";

const initialBrandForm = {
  brand_name_en: "",
  brand_logo: null,
  brand_tagline_en: "",
  brand_name_hi: "",
  brand_tagline_hi: "",
};

const normalizeBrandForm = (brand) => {
  const source = brand || {};

  return {
    brand_name_en: source.brand_name_en  || "",
    brand_logo: source.brand_logo  || null,
    brand_tagline_en: source.brand_tagline_en || "",
    brand_name_hi: source.brand_name_hi || "",
    brand_tagline_hi: source.brand_tagline_hi || "",
  };
};

export const useOfferForm = ({ brandId, selectedDomain, onSaved }) => {
  const isEdit = Boolean(brandId);
  const [form, setForm] = useState(initialBrandForm);
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!isEdit || !brandId) {
      setForm(initialBrandForm);
      return;
    }

    let cancelled = false;

    const loadBrand = async () => {
      try {
        const response = await getBrandById(brandId);
        const fetchedBrand = Array.isArray(response)
          ? response[0] ?? null
          : response || null;

        if (!cancelled) {
          setForm(fetchedBrand ? normalizeBrandForm(fetchedBrand) : initialBrandForm);
        }
      } catch (fetchError) {
        if (!cancelled) {
          setForm(initialBrandForm);
        }
      }
    };

    loadBrand();

    return () => {
      cancelled = true;
    };
  }, [brandId, isEdit]);

  const handleFieldChange = useCallback((event) => {
    const { name, value } = event.target;
    setFieldErrors((prev) => ({ ...prev, [name]: "" }));
    setForm((prev) => ({ ...prev, [name]: value }));
  }, []);

  const handleImageChange = useCallback((event) => {
    const file = event.target.files?.[0] || null;
    setFieldErrors((prev) => ({ ...prev, brand_logo: "" }));
    setForm((prev) => ({ ...prev, brand_logo: file }));
  }, []);

  const handleSubmit = useCallback(
    async (event) => {
      event.preventDefault();

      const nextFieldErrors = {};
      const brandNameEn = form.brand_name_en.trim();
      if (!brandNameEn) {
        nextFieldErrors.brand_name_en = "Brand name in English is required.";
      }

      const hasBrandLogo =
        typeof form.brand_logo === "string"
          ? form.brand_logo.trim().length > 0
          : Boolean(form.brand_logo);

      if (!isEdit && !hasBrandLogo) {
        nextFieldErrors.brand_logo = "Brand logo is required for a new brand.";
      }

      if (!selectedDomain?.id) {
        nextFieldErrors.domain = "Select a product in the header before saving the brand.";
      }

      setFieldErrors(nextFieldErrors);

      if (Object.keys(nextFieldErrors).length > 0) {
        setError(Object.values(nextFieldErrors)[0]);
        return;
      }

      const payload = buildBrandFormData({
        id: brandId,
        productId: selectedDomain.id,
        brand_name_en: form.brand_name_en.trim(),
        brand_tagline_en: form.brand_tagline_en.trim(),
        brand_name_hi: form.brand_name_hi.trim(),
        brand_tagline_hi: form.brand_tagline_hi.trim(),
        brand_logo: form.brand_logo,
      });

      setError("");
      setIsSubmitting(true);

      try {
        if (isEdit) {
          await updateBrand(brandId, payload);
        } else {
          await saveBrand(payload);
        }
        setFieldErrors({});
        onSaved?.();
      } catch (saveError) {
        const message =
          saveError?.response?.data?.message ||
          saveError?.response?.data?.error ||
          saveError?.message ||
          "Failed to save brand.";
        setError(message);
      } finally {
        setIsSubmitting(false);
      }
    },
    [brandId, form, isEdit, onSaved, selectedDomain]
  );

  return {
    isEdit,
    form,
    error,
    fieldErrors,
    isSubmitting,
    setForm,
    handleFieldChange,
    handleImageChange,
    handleSubmit,
  };
};

export default useOfferForm;
